/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  ApolloCache,
  ApolloError,
  DefaultContext,
  MutationFunctionOptions
} from "@apollo/client"
import { MaybeMasked } from "@apollo/client/masking"

import { Logger } from "shared/lib/Logger"
import { ApiResponse } from "shared/types/ApiResponse"
import { AppError } from "shared/types/error/AppError"
import { InvalidDataError } from "shared/types/error/InvalidDataError"

import z from "zod"

type ApolloMutateFunction<
  TData,
  TVariables,
  TContext = DefaultContext,
  TCache extends ApolloCache<any> = ApolloCache<any>
> = (
  options?: MutationFunctionOptions<TData, TVariables, TContext, TCache>
) => Promise<{
  data?: MaybeMasked<TData> | null
  errors?: readonly { message: string }[]
}>

export interface CallMutationOptions<
  TData,
  TVariables,
  TResult,
  TContext = DefaultContext,
  TCache extends ApolloCache<any> = ApolloCache<any>
> {
  variables: TVariables
  transform: (data: TData) => TResult
  schemas?: {
    input?: z.ZodType<TVariables>
    response?: z.ZodType<TData>
  }
  apolloOptions?: Omit<
    MutationFunctionOptions<TData, TVariables, TContext, TCache>,
    "variables"
  >
}

// Field paths and issue codes only (no localized Zod text), so the message is
// stable per failure type and can be used for grouping.
function describeIssues(stage: "input" | "response", error: z.ZodError) {
  const issues = error.issues
    .map(issue => `${issue.path.join(".") || "(root)"} [${issue.code}]`)
    .join(", ")
  return `Invalid mutation ${stage}: ${issues}`
}

function validateInput<TVariables>(
  variables: TVariables,
  schema: z.ZodType<TVariables> | undefined
): TVariables {
  if (!schema) return variables

  const result = schema.safeParse(variables)
  if (!result.success) {
    throw new InvalidDataError(describeIssues("input", result.error), {
      data: variables,
      issues: result.error.issues
    })
  }

  return result.data
}

function validateResponse<TData>(
  data: TData,
  schema: z.ZodType<TData> | undefined
): TData {
  if (!schema) return data

  const result = schema.safeParse(data)
  if (!result.success) {
    throw new InvalidDataError(describeIssues("response", result.error), {
      data,
      issues: result.error.issues
    })
  }

  return result.data
}

function handleError(error: unknown): ApiResponse<never> {
  if (error instanceof AppError) {
    error.notify()
  } else if (
    error instanceof ApolloError &&
    !error.graphQLErrors.length &&
    !error.networkError
  ) {
    // GraphQL and network errors are already reported by errorLink and fetchWithRetries
    Logger.error(error)
  }

  const message = error instanceof Error ? error.message : "Unknown error"
  return { data: null, errors: [{ message }] }
}

export async function callMutation<
  TData,
  TVariables,
  TResult,
  TContext = DefaultContext,
  TCache extends ApolloCache<any> = ApolloCache<any>
>(
  mutate: ApolloMutateFunction<TData, TVariables, TContext, TCache>,
  options: CallMutationOptions<TData, TVariables, TResult, TContext, TCache>
): Promise<ApiResponse<TResult>> {
  const { variables, transform, schemas, apolloOptions } = options

  try {
    const validatedVariables = validateInput(variables, schemas?.input)
    const response = await mutate({
      ...apolloOptions,
      variables: validatedVariables
    })

    if (response.errors?.length) {
      return {
        data: null,
        errors: response.errors.map(e => ({ message: e.message }))
      }
    }

    const rawData = response.data as TData | null | undefined
    if (!rawData) {
      return { data: null, errors: [{ message: "No data returned" }] }
    }

    const parsedData = validateResponse(rawData, schemas?.response)
    return { data: transform(parsedData) }
  } catch (error) {
    return handleError(error)
  }
}
